import React from "react";

import { faAngleDown, faEllipsisV, faPen, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UFuzzy from "@leeoniya/ufuzzy";
import cx from "classnames";

import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import { useAnalyticsTrack } from "~/meetingRecorder/utils";
import IconButton from "~/ui/button/IconButton";
import { Input } from "~/ui/legacyFellowIndex";
import { Menu } from "~/ui/menu";

import { useDeleteConversationMutation, useRenameConversationMutation } from "../AskFellowHooks";
import { AskFellowConversationSearchBar } from "./AskFellowConversationSearchBar";

import styles from "./RecentChats.module.css";

type StandardConversationType = { id: string; title: string; isSearchResult: false; updatedAt: string };
type SearchResultConversationType = {
	id: string;
	title: string | React.ReactNode;
	isSearchResult: true;
	updatedAt: string;
};
type Conversation = {
	id: string;
	title: string;
	updatedAt: string;
};
type MaybeSearchResultConversationType = StandardConversationType | SearchResultConversationType | Conversation;

// https://github.com/leeoniya/uFuzzy/blob/553f8fa1fc6941e086fdae192609a6e2315b2b7a/dist/uFuzzy.d.ts#L100-L155
const uFuzzy = new UFuzzy({
	// Expect the search term to either be at the start of the word or at some
	// alphanumeric boundary, going from number to letter or lower to upper case.
	interLft: 1,
	// Tolerate some typos
	intraMode: 1,
	intraIns: 1,
	intraSub: 1,
	intraTrn: 1,
	intraDel: 1,
});

const day = 24 * 60 * 60 * 1000;

const monthFormat = new Intl.DateTimeFormat("en-US", {
	month: "long",
});

const PAGINATION_INCREMENT = 24;

function KebabMenu({ children, handleClassName }: { children: React.ReactElement; handleClassName?: string }) {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const onMenuOpenChange = React.useCallback((open: boolean) => setIsMenuOpen(open), []);

	return (
		<Menu.Root open={isMenuOpen} onOpenChange={onMenuOpenChange}>
			<Menu.Trigger>
				<IconButton
					className={cx(styles.kebabMenu, handleClassName, styles.kebabMenuIcon, {
						[styles.kebabMenuOpen]: isMenuOpen,
					})}
					title="Menu"
					icon={faEllipsisV}
					variant="tertiary"
					size="small"
				/>
			</Menu.Trigger>
			<Menu.Content align="end">{children}</Menu.Content>
		</Menu.Root>
	);
}

type RecentChatsProps = {
	className?: string;
	conversations: ReadonlyArray<Conversation>;
	onConversationSelected?: (conversationId: string) => void;
};

function RecentChats({ className, conversations, onConversationSelected }: RecentChatsProps) {
	const [visibleCount, setVisibleCount] = React.useState(PAGINATION_INCREMENT);
	const [editingId, setEditingId] = React.useState<string | null>(null);
	const [editTitle, setEditTitle] = React.useState("");
	const hasMoreChats = conversations.length > visibleCount;
	const dispatchAnalyticsTrack = useAnalyticsTrack();
	const [deleteConversation] = useDeleteConversationMutation();
	const [renameConversation] = useRenameConversationMutation();

	const handleConversationClick = React.useCallback(
		(conversationId: string) => {
			dispatchAnalyticsTrack("[Ask Copilot] Clicked the previous conversation", {
				conversationId,
			});
			if (onConversationSelected) {
				onConversationSelected(conversationId);
			}
		},
		[dispatchAnalyticsTrack, onConversationSelected],
	);

	const handleShowMoreClick = React.useCallback(() => {
		setVisibleCount(count => Math.min(count + PAGINATION_INCREMENT, conversations.length));
		dispatchAnalyticsTrack("[Ask Copilot] Clicked to show more previous conversations");
	}, [dispatchAnalyticsTrack, conversations.length]);

	const handleStartEditing = React.useCallback((conversation: Conversation) => {
		setEditingId(conversation.id);
		setEditTitle(conversation.title);
	}, []);

	const handleEditChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setEditTitle(e.target.value);
	}, []);

	const handleEditSave = React.useCallback(
		async (conversation: Conversation) => {
			if (editTitle.trim() && editTitle !== conversation.title) {
				dispatchAnalyticsTrack("[Ask Copilot] Renamed conversation", {
					conversationId: conversation.id,
				});
				await renameConversation(conversation.id, editTitle.trim());
			}
			setEditingId(null);
		},
		[editTitle, dispatchAnalyticsTrack, renameConversation],
	);

	const handleEditCancel = React.useCallback((conversation: Conversation) => {
		setEditingId(null);
		setEditTitle(conversation.title);
	}, []);

	const handleEditKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>, conversation: Conversation) => {
			if (e.key === "Enter") {
				handleEditSave(conversation);
				e.preventDefault();
			} else if (e.key === "Escape") {
				handleEditCancel(conversation);
				e.stopPropagation();
			}
		},
		[handleEditSave, handleEditCancel],
	);

	const handleDelete = React.useCallback(
		async (conversation: Conversation) => {
			dispatchAnalyticsTrack("[Ask Copilot] Deleted conversation", {
				conversationId: conversation.id,
			});
			await deleteConversation(conversation.id);
		},
		[dispatchAnalyticsTrack, deleteConversation],
	);

	const handleInputBlur = React.useCallback(
		(conversation: Conversation) => () => handleEditSave(conversation),
		[handleEditSave],
	);
	const handleInputKeyDown = React.useCallback(
		(conversation: Conversation) => (e: React.KeyboardEvent<HTMLInputElement>) => handleEditKeyDown(e, conversation),
		[handleEditKeyDown],
	);
	const handleConversationButtonClick = React.useCallback(
		(conversationId: string) => () => handleConversationClick(conversationId),
		[handleConversationClick],
	);
	const handleStartEditingClick = React.useCallback(
		(conversation: Conversation) => () => handleStartEditing(conversation),
		[handleStartEditing],
	);
	const handleDeleteClick = React.useCallback(
		(conversation: Conversation) => () => handleDelete(conversation),
		[handleDelete],
	);

	const [searchTerm, setSearchTerm] = React.useState("");
	const debouncedSearchTerm = useDebouncedValue(searchTerm, 100);
	const isSearchActive = debouncedSearchTerm.length > 0;

	const searchFilteredAskCopilotConversations = React.useMemo((): MaybeSearchResultConversationType[] => {
		let visibleConversations = conversations.slice(0, visibleCount);

		if (!isSearchActive) {
			return visibleConversations.map(conversation => ({ ...conversation, isSearchResult: false }));
		}

		const haystack = conversations.map(c => c.title);

		let indexes = uFuzzy.filter(haystack, debouncedSearchTerm);

		if (!indexes) {
			return [];
		}
		visibleConversations = indexes.map(index => conversations[index]).slice(0, visibleCount);

		return visibleConversations.map(conversation => ({ ...conversation, isSearchResult: true }));
	}, [conversations, debouncedSearchTerm, isSearchActive, visibleCount]);

	const conversationsInGroups = React.useMemo(() => {
		const groups: { title: string; range: number; conversations: MaybeSearchResultConversationType[] }[] = [
			{ title: "Today", range: day, conversations: [] },
			{ title: "Yesterday", range: 2 * day, conversations: [] },
			{ title: "Last 7 days", range: 7 * day, conversations: [] },
			{ title: "Previous 30 days", range: 30 * day, conversations: [] },
		];

		const sortedAskCopilotConversations = [...searchFilteredAskCopilotConversations].sort(
			(a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
		);
		const now = Date.now();

		for (const conversation of sortedAskCopilotConversations) {
			const diff = now - Date.parse(conversation.updatedAt);
			const group = groups.find(group => diff <= group.range);
			if (group) {
				group.conversations.push(conversation);
			} else {
				const date = new Date(Date.parse(conversation.updatedAt));
				const startOfMonth = (() => {
					const clonedDate = new Date(date.getTime());
					clonedDate.setDate(0);
					clonedDate.setHours(0, 0, 0, 0);
					return clonedDate;
				})();

				groups.push({
					title: monthFormat.format(date),
					range: now - startOfMonth.getTime(),
					conversations: [conversation],
				});
			}
		}

		return groups.filter(group => group.conversations.length > 0);
	}, [searchFilteredAskCopilotConversations]);

	if (!conversations.length) {
		return null;
	}

	return (
		<>
			<div className={styles.recentChatsSearchBar}>
				<AskFellowConversationSearchBar value={searchTerm} onValueChange={setSearchTerm} />
			</div>
			<div className={cx(styles.recentChatsContainer, className)}>
				<div className={styles.recentChatsList}>
					{conversationsInGroups.map((group, index) => (
						<React.Fragment key={group.title}>
							<span className={styles.recentChatGroupTitle}>{group.title}</span>
							{group.conversations.map(conversation =>
								editingId === conversation.id ? (
									<div key={conversation.id} className={styles.editingRecentChatItemGroup}>
										<Input
											className={styles.titleTextInput}
											value={editTitle}
											onChange={handleEditChange}
											onBlur={handleInputBlur(conversation as Conversation)}
											onKeyDown={handleInputKeyDown(conversation as Conversation)}
											autoFocus
											name="conversation_title_edit"
										/>
									</div>
								) : (
									<div key={conversation.id} className={styles.recentChatItemGroup}>
										<button className={styles.recentChatItem} onClick={handleConversationButtonClick(conversation.id)}>
											<span className={styles.recentChatTitle}>{conversation.title || "Untitled chat"}</span>
										</button>
										<KebabMenu handleClassName={styles.kebabMenu}>
											<>
												<Menu.Item onClick={handleStartEditingClick(conversation as Conversation)}>
													<FontAwesomeIcon fixedWidth icon={faPen} /> Rename
												</Menu.Item>
												<Menu.Item
													destructive
													className={styles.destructiveOption}
													onClick={handleDeleteClick(conversation as Conversation)}
												>
													<FontAwesomeIcon fixedWidth icon={faTrashCan} /> Delete
												</Menu.Item>
											</>
										</KebabMenu>
									</div>
								),
							)}
							{index < conversationsInGroups.length - 1 && (
								<span key={`span-divider-${group.title}`} className={styles.recentChatGroupDivider} />
							)}
						</React.Fragment>
					))}

					{hasMoreChats && (
						<button className={styles.showAllButton} onClick={handleShowMoreClick}>
							Show more <FontAwesomeIcon icon={faAngleDown} />
						</button>
					)}
				</div>
			</div>
		</>
	);
}

export default RecentChats;
